import { useMemo, useState } from 'react';
import { Card } from '../../components';
import {
  ScrubChart,
  type ScrubMarker,
  type ScrubPoint,
} from '../../components/charts/ScrubChart';
import { ScrubHeader } from '../../components/charts/ScrubHeader';
import { useAppData } from '../../data';
import { computeSafeToSpend } from '../../lib/cashPosition';
import { formatCurrency } from '../../lib/currency';
import { fromIsoDate, toIsoDate } from '../../lib/date';
import {
  buildForecast,
  computeAvgDailySpend,
  type ForecastDayDetailed,
} from '../../lib/forecast';
import { getPayPeriod } from '../../lib/payPeriod';

type Range = 7 | 30 | 60 | 90;

const CHIPS: Range[] = [7, 30, 60, 90];

function startBalanceFromCashPosition(data: ReturnType<typeof useAppData>): number {
  const accounts = data.accounts ?? [];
  if (accounts.length === 0) return 0;
  const t = new Date();
  const period = getPayPeriod(data.profile, t);
  const r = computeSafeToSpend(
    accounts,
    data.scheduledItems,
    data.transactions,
    toIsoDate(period.start),
    toIsoDate(period.end),
    0,
  );
  return r.checkingTotal - r.spendingSinceUpdate;
}

function formatShortDate(iso: string): string {
  return fromIsoDate(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function hasEventOfType(
  d: ForecastDayDetailed,
  t: 'income' | 'bill',
): boolean {
  return d.events.some((e) => e.type === t);
}

/**
 * 7/30/60/90-day cash-flow forecast. Walks scheduled income + bills
 * forward and applies a daily-average drag for variable spend, plotting
 * end-of-day balance with finger-scrub. Income/bill days surface as
 * arrow markers on the line.
 */
export function ForecastCard() {
  const data = useAppData();
  const [range, setRange] = useState<Range>(30);
  const [scrub, setScrub] = useState<ScrubPoint | null>(null);

  const startBalance = useMemo(
    () => startBalanceFromCashPosition(data),
    [data],
  );

  const avgSpend = useMemo(
    () =>
      computeAvgDailySpend(
        data.transactions,
        new Date().toISOString().slice(0, 10),
        30,
      ),
    [data.transactions],
  );

  const result = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return buildForecast({
      startBalance,
      startDate: todayIso,
      days: range,
      scheduledIncome: data.scheduledItems.filter((s) => s.type === 'income'),
      scheduledBills: data.scheduledItems.filter((s) => s.type === 'expense'),
      avgDailySpend: avgSpend,
      includeAvgSpend: true,
    });
  }, [startBalance, range, data.scheduledItems, avgSpend]);

  const currency = data.profile.currency;
  const fmt = (n: number) => formatCurrency(n, { currency });

  const series = useMemo<ScrubPoint[]>(
    () => result.days.map((d) => ({ time: d.date, value: d.balanceEnd })),
    [result.days],
  );

  const markers = useMemo<ScrubMarker[]>(() => {
    const out: ScrubMarker[] = [];
    for (const d of result.days) {
      if (hasEventOfType(d, 'income')) {
        out.push({
          time: d.date,
          position: 'belowBar',
          color: '#0EA66B',
          shape: 'arrowUp',
        });
      }
      if (hasEventOfType(d, 'bill')) {
        out.push({
          time: d.date,
          position: 'aboveBar',
          color: '#E5484D',
          shape: 'arrowDown',
        });
      }
    }
    return out;
  }, [result.days]);

  // Sparse-data empty state.
  const hasScheduled = data.scheduledItems.length > 0;
  const hasRecentTx = data.transactions.length > 0;
  if (!hasScheduled && !hasRecentTx) {
    return (
      <Card elevation="sm" className="p-5 rounded-[24px]">
        <Header title="Next 30 days" subtitle="Projected balance" />
        <p className="py-6 text-center text-[13px] text-text-secondary">
          Add bills and income on the Plan tab to see your forecast.
        </p>
      </Card>
    );
  }

  // Color rule: any day < 0 → red; any day in $0-$100 → amber; else green.
  const minBal = result.minBalance;
  const lineColor =
    minBal < 0 ? '#E5484D' : minBal < 100 ? '#E8B339' : '#0EA66B';

  const endIso = result.days[result.days.length - 1]?.date ?? '';
  const netChange = result.endingBalance - startBalance;

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <div className="flex items-start justify-between mb-3">
        <ScrubHeader
          title="Cash flow forecast"
          subtitle={endIso ? `Through ${formatShortDate(endIso)}` : undefined}
          currentValue={result.endingBalance}
          currentLabel={endIso ? formatShortDate(endIso) : 'Forecast'}
          scrubValue={scrub?.value ?? null}
          scrubLabel={scrub ? formatShortDate(scrub.time) : null}
          format={fmt}
          color={lineColor}
          delta={{ value: netChange, period: `next ${range}d` }}
        />
        <RangeChips value={range} onChange={setRange} />
      </div>

      {result.daysBelowZero > 0 ? (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-pill bg-negative/15 px-2.5 py-1 text-[11px] font-medium text-negative">
          Projected to go negative on {formatShortDate(result.minBalanceDate)}
        </div>
      ) : null}

      <ScrubChart
        data={series}
        height={220}
        color={lineColor}
        areaFill
        markers={markers}
        onScrub={setScrub}
      />

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat
          label={`Min · ${formatShortDate(result.minBalanceDate)}`}
          value={fmt(result.minBalance)}
          tone={result.minBalance <= 0 ? 'negative' : undefined}
        />
        <Stat
          label="Ending"
          value={fmt(result.endingBalance)}
          tone={result.endingBalance < 0 ? 'negative' : undefined}
        />
        <Stat
          label="Net change"
          value={`${netChange >= 0 ? '+' : ''}${fmt(netChange)}`}
          tone={netChange < 0 ? 'negative' : 'positive'}
        />
      </div>
    </Card>
  );
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <div className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
        {title}
      </div>
      <div className="text-[13px] text-text-secondary">{subtitle}</div>
    </div>
  );
}

function RangeChips({
  value,
  onChange,
}: {
  value: Range;
  onChange: (v: Range) => void;
}) {
  return (
    <div
      role="tablist"
      className="inline-flex p-0.5 rounded-pill bg-surface-secondary text-[11px] font-medium shrink-0"
    >
      {CHIPS.map((v) => (
        <button
          key={v}
          role="tab"
          type="button"
          aria-selected={value === v}
          onClick={() => onChange(v)}
          className={`px-2.5 py-0.5 rounded-pill ${
            value === v
              ? 'bg-surface text-text-primary shadow-sm'
              : 'text-text-secondary'
          }`}
        >
          {v}d
        </button>
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'positive' | 'negative';
}) {
  const colorClass =
    tone === 'positive'
      ? 'text-positive'
      : tone === 'negative'
        ? 'text-negative'
        : 'text-text-primary';
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-[0.05em] text-text-tertiary truncate">
        {label}
      </div>
      <div className={`mt-0.5 text-[14px] font-mono tabular-nums ${colorClass}`}>
        {value}
      </div>
    </div>
  );
}
